import {
  DEFAULT_DEPARTMENT,
  DEFAULT_STATUS_CHIP_META,
  RESERVATION_MODE,
  RESERVATION_STATUS,
  STATUS_CHIP_META,
  STATUS_KEYWORD,
} from "../constants";
import { getTomorrowDate } from "../components/ReservationFlow/constants";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Días desde hoy hasta `date` (redondeo hacia arriba). Lo usa la UI para avisar
// de una cancelación tardía (penalización); el backend decide el resto.
export const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / MS_PER_DAY);

// Editable / cancelable: el backend ya computa estos flags (campos editable y
// cancellable del DTO). El front solo los lee, no reconstruye la lógica de workflow.
export const isReservationEditable = (reservation) => !!reservation?.editable;

export const canCancelReservation = (reservation) => !!reservation?.cancellable;

// Meta del Chip de estado (clave i18n + color), con fallback.
export const getStatusChipMeta = (status) => STATUS_CHIP_META[status] ?? DEFAULT_STATUS_CHIP_META;

// Fecha → "yyyy-MM-dd" ("" si no hay fecha).
export const getFormattedDate = (date) =>
  date ? new Date(date).toISOString().split("T")[0] : "";

// Clasifica un estado (enum) en variante de UI
export const statusVariant = (status) => {
  if (status?.includes(STATUS_KEYWORD.PENDING)) return "warning";
  if (status?.includes(STATUS_KEYWORD.APPROVED)) return "success";
  return "error";
};

// Estado inicial del wizard (crear o editar) a partir del modelo limpio del backend.
export const buildInitialReservationData = (mode, initialData, vacationHomes) => {
  if (mode === RESERVATION_MODE.EDIT && initialData) {
    const assignedRooms = initialData.assignedRooms || [];
    const location = vacationHomes.find((loc) => loc.name === initialData.property)?.name || "";
    return {
      location,
      startDate: initialData.startDate ? getFormattedDate(initialData.startDate) : getTomorrowDate(),
      endDate: initialData.endDate ? getFormattedDate(initialData.endDate) : getTomorrowDate(),
      numberOfGuests: initialData.totalPeople || 0,
      numberOfRooms: assignedRooms.length || 0,
      assignedRooms,
      status: initialData.status || RESERVATION_STATUS.PENDING_CEO,
      isAvailabilityChecked: false,
    };
  }
  return {
    location: "",
    startDate: getTomorrowDate(),
    endDate: getTomorrowDate(),
    numberOfGuests: 0,
    numberOfRooms: 0,
    assignedRooms: [],
    status: RESERVATION_STATUS.PENDING_CEO,
    isAvailabilityChecked: false,
  };
};

export const buildReservationPayload = (reservationData, userData, selectedLocation, mode, initialData) => ({
  id: initialData?.id,
  department: userData.department || DEFAULT_DEPARTMENT,
  property: selectedLocation.name,
  startDate: getFormattedDate(reservationData.startDate),
  endDate: getFormattedDate(reservationData.endDate),
  totalPeople: reservationData.numberOfGuests,
  assignedRooms: reservationData.assignedRooms,
});

// Valida que la cantidad de huéspedes entre en las habitaciones elegidas.
export const validateRoomDistribution = (reservationData, location, t) => {
  const { numberOfRooms, numberOfGuests } = reservationData;
  if (!location) return false;

  const capacities = location.capacities.map(Number);
  const maxRooms = location.availableRooms;
  const totalCapacity = capacities.reduce((sum, capacity) => sum + capacity, 0);
  const selectedCapacities = [...capacities].sort((a, b) => b - a).slice(0, numberOfRooms);
  const totalCapacitySelected = selectedCapacities.reduce((sum, capacity) => sum + capacity, 0);

  if (numberOfRooms <= 0) throw new Error(t("errorNumberOfRooms"));
  if (numberOfRooms > maxRooms) throw new Error(t("errorMaxRooms", { maxRooms }));
  if (numberOfGuests <= 0) throw new Error(t("errorNoGuests"));
  if (numberOfGuests > totalCapacity) {
    throw new Error(t("errorExceedsTotalCapacity", { guests: numberOfGuests, max: totalCapacity }));
  }
  if (numberOfGuests > totalCapacitySelected) {
    throw new Error(
      t("errorNotEnoughRoomsSelected", { guests: numberOfGuests, numberOfRooms, max: totalCapacity })
    );
  }

  let guestsRemaining = numberOfGuests;
  for (const capacity of selectedCapacities) {
    guestsRemaining -= Math.min(capacity, guestsRemaining);
  }
  if (guestsRemaining > 0) {
    throw new Error(t("errorDistributionImpossible", { guests: numberOfGuests, rooms: numberOfRooms }));
  }

  return true;
};
